import { Rect } from '@int/geotoolkit/util/Rect';
import { WellLogWidget } from '@int/geotoolkit/welllog/widgets/WellLogWidget';
import { LogAbstractVisual } from '@int/geotoolkit/welllog/LogAbstractVisual';
import { WellBoreNode } from '@int/geotoolkit/schematics/scene/WellBoreNode';
import { TrackType } from '@int/geotoolkit/welllog/TrackType';
import { SCHEMATIC_FORM_VALUES } from 'features/wells/schematics/helpers/constants';

import { CONVERT_SYSTEMS, UNITS } from 'app/app.constants';

import { object, string, number } from 'yup';

export let leftRectangle;
export let rightRectangle;

const units = {
  [CONVERT_SYSTEMS.METRIC]: {
    length: UNITS.METER,
    width: UNITS.MILLIMETER,
  },
  [CONVERT_SYSTEMS.IMPERIAL]: {
    length: UNITS.FEET,
    width: UNITS.INCHES,
  },
};

export const setLeftRectangle = (left) => (leftRectangle = left);

export const setRightRectangle = (right) => (rightRectangle = right);

const getGeometryProperty = (geometry, propertyName) => {
  if (Array.isArray(geometry)) {
    geometry = geometry[0];
  }
  return geometry[propertyName];
};

export const highlightComponentNode = (
  componentNode,
  wellBoreNode,
  highlightLayer,
) => {
  if (componentNode === undefined) {
    return;
  }
  let bounds = componentNode.getBounds();
  const diameters = getGeometryProperty(
    componentNode.getData()['geometry'],
    'diameter',
  );

  if (diameters['inner'] === undefined) {
    bounds = wellBoreNode.getSceneTransform().transform(bounds);
    bounds = highlightLayer.getSceneTransform().inverseTransform(bounds);
    leftRectangle.setBounds(bounds);
    leftRectangle.setVisible(true);
  } else {
    const width = (diameters['outer'] - diameters['inner']) / 2;
    let leftBounds = new Rect(
      bounds.getLeft(),
      bounds.getTop(),
      bounds.getLeft() + width,
      bounds.getTop() + bounds.getHeight(),
    );
    let rightBounds = new Rect(
      bounds.getRight() - width,
      bounds.getTop(),
      bounds.getRight(),
      bounds.getTop() + bounds.getHeight(),
    );
    leftBounds = wellBoreNode.getSceneTransform().transform(leftBounds);
    leftBounds = highlightLayer
      .getSceneTransform()
      .inverseTransform(leftBounds);
    rightBounds = wellBoreNode.getSceneTransform().transform(rightBounds);
    rightBounds = highlightLayer
      .getSceneTransform()
      .inverseTransform(rightBounds);
    leftRectangle.setBounds(leftBounds);
    rightRectangle.setBounds(rightBounds);
    leftRectangle.setVisible(true);
    rightRectangle.setVisible(true);
  }
};

export const createWellLogWidget = function (wellBoreNode, geometryBounds) {
  const widget = new WellLogWidget({
    header: { visible: false },
    footer: { visible: false },
    border: { visible: false },
    horizontalscrollable: false,
    verticalscrollable: false,
    nodefilter: (node) =>
      node instanceof LogAbstractVisual || node instanceof WellBoreNode,
    tools: {
      cursortracking: {
        tooltip: {
          enabled: true,
        },
      },
    },
  }).setDepthLimits(geometryBounds.getTop(), geometryBounds.getBottom());
  widget
    .addTrack(TrackType.AnnotationTrack)
    .setWidth(405)
    .addChild([wellBoreNode]);
  return widget;
};

export function compareNumbers(a, b) {
  return a.data.geometry.depth.from.value - b.data.geometry.depth.from.value;
}

export function compareDates(a, b) {
  return new Date(a.validFrom) - new Date(b.validFrom);
}

export const formDataMapper = (data) => ({
  name: data.component,
  data: {
    description: data.description,
    geometry: {
      depth: {
        from: data.startDepth,
        to: data.endDepth,
      },
      diameter: {
        outer: data.outerDiameter,
        inner: data.innerDiameter,
      },
    },
  },
});

export const wellBoreDataMapper = (data) => {
  if (data === null || data === undefined || data.length === 0) {
    return null;
  }
  return data.map(({ name, data }) => {
    const { description, geometry } = data;
    const { depth, diameter } = geometry;
    return {
      name,
      data: {
        description,
        geometry: {
          depth: {
            from: depth.from.value,
            to: depth.to.value,
          },
          diameter: {
            outer: diameter.outer.value,
            inner: diameter.inner.value,
          },
        },
      },
    };
  });
};

export const initialFormValueMapper = (rawData) => {
  if (rawData.data === undefined || rawData === null) return null;

  const { data, name } = rawData;
  const { description, geometry } = data;
  const { depth, diameter } = geometry;

  return {
    [SCHEMATIC_FORM_VALUES.COMPONENT]: name,
    [SCHEMATIC_FORM_VALUES.DESCRIPTION]: description,
    [SCHEMATIC_FORM_VALUES.END_DEPTH]: depth.to,
    [SCHEMATIC_FORM_VALUES.INNER_DIAMETER]: diameter.inner,
    [SCHEMATIC_FORM_VALUES.OUTER_DIAMETER]: diameter.outer,
    [SCHEMATIC_FORM_VALUES.START_DEPTH]: depth.from,
  };
};

export const newSchematicVersionComponentsMapper = (
  data,
  lengthUnit,
  widthUnit,
) => {
  if (data === null || data === undefined || data.length === 0) {
    return [];
  }
  return data.map(({ name, data }) => {
    const { description, geometry } = data;
    const { depth, diameter } = geometry;
    return {
      name,
      description,
      depthFrom: {
        value: depth.from,
        unit: lengthUnit,
        roundedValue: 0,
      },
      depthTo: {
        value: depth.to,
        unit: lengthUnit,
        roundedValue: 0,
      },
      outerDiameter: {
        value: diameter.outer,
        unit: widthUnit,
        roundedValue: 0,
      },
      innerDiameter: {
        value: diameter.inner,
        unit: widthUnit,
        roundedValue: 0,
      },
    };
  });
};

export const getFormSchema = () =>
  object().shape({
    [SCHEMATIC_FORM_VALUES.COMPONENT]: string()
      .required('Component name is required')
      .nullable(),
    [SCHEMATIC_FORM_VALUES.START_DEPTH]: number().required(
      'Start depth is required',
    ),
    [SCHEMATIC_FORM_VALUES.END_DEPTH]: number()
      .required('End depth is required')
      .test(
        'greater-than-start-depth',
        'End depth must be greater than Start depth',
        function (value) {
          return value > this.parent[SCHEMATIC_FORM_VALUES.START_DEPTH];
        },
      ),
    [SCHEMATIC_FORM_VALUES.INNER_DIAMETER]: number().required(
      'Inner Diameter is required',
    ),
    [SCHEMATIC_FORM_VALUES.OUTER_DIAMETER]: number()
      .required('Outer Diameter is required')
      .test(
        'greater-than-outer-diameter',
        'Outer Diameter must be greater than Inner Diameter',
        function (value) {
          return value > this.parent[SCHEMATIC_FORM_VALUES.INNER_DIAMETER];
        },
      ),
  });

export const schematicUoM = (unitSystem) => {
  if (unitSystem === undefined) return units[CONVERT_SYSTEMS.METRIC];
  return units[unitSystem];
};

export const getInitials = (fullName) => {
  if (!fullName) return '';

  const [lastName, firstName] = fullName.split(',').map((part) => part.trim());

  if (!firstName || !lastName) return '';

  return `${lastName[0]}${firstName[0]}`.toUpperCase();
};
